<template>
    <v-container fluid>
        <v-card>
            <v-overlay :value="loading" absolute>
                <div class="text-center">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        :size="50"
                        class="my-4"
                    ></v-progress-circular>
                    <p class="display-1">{{ $t("Loading") }}</p>
                </div>
            </v-overlay>
                <v-card-title class="d-flex justify-space-between align-center pt-3">
                    <span>{{ title }}</span>
                    <div>
                        <v-btn color="secundary" @click.prevent="close">
                        {{ $t("Cancel") }}
                        </v-btn>
                        <v-btn color="primary" @click.prevent="saveItem">
                        {{ $t("Save") }}
                        </v-btn>
                    </div>
                    </v-card-title>
            <v-card-text class="px-5">
                <v-form class="mt-5">
                    <v-text-field
                        v-bind:label="$t('Description')"
                        v-model="documentLifecycle.description"
                        :error-messages="descriptionErrors"
                        @input="$v.documentLifecycle.description.$touch()"
                        @blur="$v.documentLifecycle.description.$touch()"
                    ></v-text-field>
                    <v-row>
                        <v-col cols="12" md="6">
                        <v-select
                            :items="contentTypeList"
                            v-model="documentLifecycle.contentTypeId"
                            :label="$t('ContentType')"
                            dense
                            item-text="text"
                            item-value="id"
                            @change="loadFieldList"
                            :error-messages="contentTypeErrors"
                            @input="$v.documentLifecycle.contentTypeId.$touch()"
                            @blur="$v.documentLifecycle.contentTypeId.$touch()"
                        ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                        <v-select
                            v-model="documentLifecycle.defFieldCode"
                            :items="fieldList"
                            :loading="fieldCodeLoading"
                            :label="$t('Field')"
                            item-text="Name"
                            item-value="Code"
                            dense
                            :error-messages="defFieldCodeErrors"
                            @input="$v.documentLifecycle.contentTypeId.$touch()"
                            @blur="$v.documentLifecycle.contentTypeId.$touch()"
                        ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="6">
                        <v-text-field
                            v-bind:label="`${$t('PreservationTime')} (${$t('Months')})`"
                            type="number"
                            dense
                            v-model="documentLifecycle.preservationMonths"
                            :error-messages="timeErrors"
                            @input="$v.documentLifecycle.preservationMonths.$touch()"
                            @blur="$v.documentLifecycle.preservationMonths.$touch()"
                        ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                        <v-select
                            :items="actions"
                            v-model="documentLifecycle.type"
                            item-text="text"
                            item-value="id"
                            :label="$t('Action')"
                            dense
                            :error-messages="typeErrors"
                            @input="$v.documentLifecycle.type.$touch()"
                            @blur="$v.documentLifecycle.type.$touch()"
                        ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                        <v-text-field
                            readonly
                            v-model="documentLifecycle.displayConditionStr"
                            :label="$t('Condition')"
                            :append-icon="
                            documentLifecycle.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'
                            "
                            @click:append="editCondition()"
                        ></v-text-field>
                        </v-col>
                    </v-row>
                    </v-form>
            </v-card-text>
        </v-card>
        <SearchCriteriaBuilderModal
            :ref="conditionModalKey"
            :contentTypeIDs="[documentLifecycle.contentTypeId]"
            @on-condition-saved="onConditionChanged"
        />
    </v-container>
</template>
<script>
import { required, minValue, maxValue, integer } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

import ContentTypeService from '@/services/ContentTypeService';
import SearchCriteriaBuilderModal from '@/components/search/SearchCriteriaBuilderModal.vue';
import DocumentLifecycleService from '@/services/DocumentLifecycleService';
import DocumentLifecycle from '@/ecm/models/DocumentLifecycle';
import StandardFieldCode from '@/ecm/variables/StandardFieldCode';
import Utils from '@/ecm/common/Utils';
import SearchUtils from '@/components/search/utilsSearch/UtilsSearch.js';

export default {
    components: {
        SearchCriteriaBuilderModal,
    },
    created() {
        this.contentTypeList = this.$store.getters['account/userContentTypes'];
        this.$v.$reset();
        const id = this.$route.params.id;
        if (id > 0) {
            this.edit(id);
        } else {
            this.new();
        }
    },
    data() {
        return {
            contentTypeList: [],
            fieldList: [],
            fieldCodeLoading: false,
            actions: [
                { id: 1, text: this.$t('Archive') },
                { id: 2, text: this.$t('Delete') },
            ],
            open: false,
            documentLifecycle: new DocumentLifecycle(),
            conditionModalKey: 'conditionModalKey',
            isNew: true,
            loading: false
        };
    },
    mixins: [validationMixin],
    validations: {
        documentLifecycle: {
            contentTypeId: { required },
            type: { required },
            defFieldCode: { required },
            description: { required },
            preservationMonths: {
                required,
                minValue: minValue(1),
                maxValue: maxValue(1200),
                integer
            },
        },
    },
    computed: {
        conditionModalInstance() {
            return this.$refs[this.conditionModalKey];
        },
        title() {
            return (
                this.$t('DocumentLifecycle') +
                (this.documentLifecycle.description
                    ? ' - ' + this.documentLifecycle.description
                    : '')
            );
        },
        descriptionErrors() {
            const errors = [];
            if (!this.$v.documentLifecycle.description.$dirty) return errors;
            !this.$v.documentLifecycle.description.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        contentTypeErrors() {
            const errors = [];
            if (!this.$v.documentLifecycle.contentTypeId.$dirty) return errors;
            !this.$v.documentLifecycle.contentTypeId.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        typeErrors() {
            const errors = [];
            if (!this.$v.documentLifecycle.type.$dirty) return errors;
            !this.$v.documentLifecycle.type.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        defFieldCodeErrors() {
            const errors = [];
            if (!this.$v.documentLifecycle.defFieldCode.$dirty) return errors;
            !this.$v.documentLifecycle.defFieldCode.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        timeErrors() {
            const errors = [];
            if (!this.$v.documentLifecycle.preservationMonths.$dirty) return errors;
            !this.$v.documentLifecycle.preservationMonths.required &&
                errors.push(this.$t('RequiredField'));
            !this.$v.documentLifecycle.preservationMonths.minValue &&
                errors.push(`${this.$t('MinValue')}: 1`);
            !this.$v.documentLifecycle.preservationMonths.maxValue &&
                errors.push(`${this.$t('MaxValue')}: 1200`);
            !this.$v.documentLifecycle.preservationMonths.integer &&
                errors.push(this.$t('IntegerValues'));

            return errors;
        },
    },

    methods: {
        loadFieldList() {
            this.fieldCodeLoading = true;
            ContentTypeService.getFields(this.documentLifecycle.contentTypeId).then(
                (data) => {
                    this.fieldList = data.filter((f) => f.FieldType == 1);
                    this.fieldList.push({
                        Code: StandardFieldCode.CreationDate,
                        Name: this.$t('CreationDate'),
                    });
                    this.fieldCodeLoading = false;
                }
            );
        },
        editCondition() {
            this.conditionModalInstance.edit(this.documentLifecycle.conditionStr);
        },
        onConditionChanged(condition) {
            this.documentLifecycle.conditionStr = condition.conditionStr;
            this.documentLifecycle.displayConditionStr = condition.displayConditionStr;
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.loading = true;
            if (this.isNew) {
                DocumentLifecycleService.create(this.documentLifecycle.toDto())
                    .then((result) => {
                        if (result) {
                            Utils.toast.success(
                                this.$t('DocumentLifecycle') + ' ' + this.$t('CreatedSuccessfully')
                            );
                            this.close();
                        }
                    })
                    .catch(() => {
                    // error
                    })
                    .then(() => {
                        //always
                        this.loading = false;
                    });
            } else {
                DocumentLifecycleService.update(this.documentLifecycle.id, this.documentLifecycle.toDto())
                    .then((result) => {
                        if (result) {
                            Utils.toast.success(
                                this.$t('DocumentLifecycle') + ' ' + this.$t('UpdatedSuccessfully')
                            );
                            this.close();
                        }
                    })
                    .catch(() => {
                    // error
                    })
                    .then(() => {
                        //always
                        this.loading = false;
                    });
            }
        },
        new() {
            this.documentLifecycle = new DocumentLifecycle();
            this.$v.$reset();
        },
        async edit(id) {
            this.loading = true;
            const result = await DocumentLifecycleService.getById(id);
            this.documentLifecycle = new DocumentLifecycle(result);
            const context = this;
            this.documentLifecycle.displayConditionStr = await SearchUtils.GetDisplayConditionStr(this.documentLifecycle.conditionStr, this.documentLifecycle.fieldsMap, context);
            this.documentLifecycle.conditionStr = SearchUtils.SetUserFormatValueIntoConditionStr(this.documentLifecycle.conditionStr);
            this.isNew = false;
            this.loadFieldList();
            this.loading = false;
        },
        close() {
            this.isNew = true;
            this.$router.push('/administration/document-lifecycle');
        },
    },
};
</script>
